// Pure helpers over the nested Project hierarchy. All mutations return
// a new Project (immutable updates) so React Query / useMemo can cheap-
// equality-check it. Callers write `await coll("projects").update(p._id,
// { garniturs: newProj.garniturs, _nextLocalId: newProj._nextLocalId })`
// to persist.

import type {
  Cabinet,
  Garnitur,
  GarniturType,
  Id,
  Project,
} from "@/lib/types";

// ── Identity ─────────────────────────────────────────────────────

/** Next project-local id. Cabinets and garniturs share one counter so
 *  selection (which is just a Set<number>) can't ambiguate between them. */
export function nextLocalId(p: Project): { id: Id; next: number } {
  const cur = p._nextLocalId ?? 1;
  return { id: cur, next: cur + 1 };
}

// ── Read-side selectors ──────────────────────────────────────────

/** All cabinets in the project, flattened. Order: garnitur order, then
 *  in-garnitur order. The garnitur each cabinet came from is NOT
 *  attached — use `findCabinetWithGarnitur` if you need the parent. */
export function allCabinets(p: Project | null | undefined): Cabinet[] {
  if (!p?.garniturs) return [];
  const out: Cabinet[] = [];
  for (const g of p.garniturs) {
    if (!g.cabinets) continue;
    for (const c of g.cabinets) out.push(c);
  }
  return out;
}

/** Find a cabinet by its project-local _id. Returns null if not present. */
export function findCabinet(p: Project | null | undefined, cabinetId: Id): Cabinet | null {
  if (!p?.garniturs) return null;
  for (const g of p.garniturs) {
    for (const c of g.cabinets ?? []) {
      if (c._id === cabinetId) return c;
    }
  }
  return null;
}

/** Find a cabinet AND its containing garnitur. Useful for badge colors
 *  and "which run is this in?" UI. */
export function findCabinetWithGarnitur(
  p: Project | null | undefined,
  cabinetId: Id,
): { cabinet: Cabinet; garnitur: Garnitur } | null {
  if (!p?.garniturs) return null;
  for (const g of p.garniturs) {
    for (const c of g.cabinets ?? []) {
      if (c._id === cabinetId) return { cabinet: c, garnitur: g };
    }
  }
  return null;
}

/** Find a garnitur by id. */
export function findGarnitur(p: Project | null | undefined, garniturId: Id): Garnitur | null {
  if (!p?.garniturs) return null;
  return p.garniturs.find((g) => g._id === garniturId) ?? null;
}

/** Count cabinets per garnitur, keyed by garnitur._id. */
export function cabinetCountsByGarnitur(p: Project | null | undefined): Record<Id, number> {
  const out: Record<Id, number> = {};
  if (!p?.garniturs) return out;
  for (const g of p.garniturs) {
    out[g._id] = g.cabinets?.length ?? 0;
  }
  return out;
}

// ── Write-side helpers ───────────────────────────────────────────

/** Add a new garnitur. Returns the new project + the assigned id. */
export function addGarnitur(
  p: Project,
  init: { name: string; type: GarniturType; color?: string; notes?: string },
): { project: Project; garniturId: Id } {
  const { id, next } = nextLocalId(p);
  const g: Garnitur = {
    _id: id,
    name: init.name,
    type: init.type,
    color: init.color,
    notes: init.notes,
    cabinets: [],
  };
  return {
    project: { ...p, garniturs: [...(p.garniturs ?? []), g], _nextLocalId: next },
    garniturId: id,
  };
}

/** Remove a garnitur AND every cabinet inside it. */
export function removeGarnitur(p: Project, garniturId: Id): Project {
  return {
    ...p,
    garniturs: (p.garniturs ?? []).filter((g) => g._id !== garniturId),
  };
}

/** Patch garnitur fields (name, type, color, notes). Does NOT replace
 *  `cabinets` — use the cabinet helpers for that. */
export function updateGarnitur(
  p: Project,
  garniturId: Id,
  patch: Partial<Omit<Garnitur, "_id" | "cabinets">>,
): Project {
  return {
    ...p,
    garniturs: (p.garniturs ?? []).map((g) =>
      g._id === garniturId ? { ...g, ...patch } : g,
    ),
  };
}

/** Add a cabinet to a specific garnitur. Auto-assigns the cabinet's _id. */
export function addCabinet(
  p: Project,
  garniturId: Id,
  init: Omit<Cabinet, "_id">,
): { project: Project; cabinetId: Id } {
  const { id, next } = nextLocalId(p);
  const c: Cabinet = { _id: id, ...init };
  return {
    project: {
      ...p,
      garniturs: (p.garniturs ?? []).map((g) =>
        g._id === garniturId ? { ...g, cabinets: [...(g.cabinets ?? []), c] } : g,
      ),
      _nextLocalId: next,
    },
    cabinetId: id,
  };
}

/** Update one cabinet's fields in-place (returns a new project tree). */
export function updateCabinet(
  p: Project,
  cabinetId: Id,
  patch: Partial<Omit<Cabinet, "_id">>,
): Project {
  return {
    ...p,
    garniturs: (p.garniturs ?? []).map((g) => ({
      ...g,
      cabinets: (g.cabinets ?? []).map((c) =>
        c._id === cabinetId ? { ...c, ...patch } : c,
      ),
    })),
  };
}

/** Update many cabinets at once. `patcher(cabinet)` returns `null` to
 *  leave a cabinet untouched, or a partial Cabinet to merge. */
export function updateCabinetsWhere(
  p: Project,
  patcher: (cabinet: Cabinet) => Partial<Omit<Cabinet, "_id">> | null,
): Project {
  return {
    ...p,
    garniturs: (p.garniturs ?? []).map((g) => ({
      ...g,
      cabinets: (g.cabinets ?? []).map((c) => {
        const patch = patcher(c);
        return patch ? { ...c, ...patch } : c;
      }),
    })),
  };
}

/** Remove a cabinet by id. */
export function removeCabinet(p: Project, cabinetId: Id): Project {
  return {
    ...p,
    garniturs: (p.garniturs ?? []).map((g) => ({
      ...g,
      cabinets: (g.cabinets ?? []).filter((c) => c._id !== cabinetId),
    })),
  };
}

/** Remove every cabinet whose id is in `ids`. Single tree walk; cheaper
 *  than chaining `removeCabinet`. */
export function removeCabinets(p: Project, ids: ReadonlySet<Id>): Project {
  return {
    ...p,
    garniturs: (p.garniturs ?? []).map((g) => ({
      ...g,
      cabinets: (g.cabinets ?? []).filter((c) => !ids.has(c._id)),
    })),
  };
}

/** Move a cabinet from its current garnitur to `targetGarniturId`. No-op
 *  if it's already there. Used for drag-between-runs in the inspector. */
export function moveCabinetToGarnitur(
  p: Project,
  cabinetId: Id,
  targetGarniturId: Id,
): Project {
  let moved: Cabinet | null = null;
  const stripped = (p.garniturs ?? []).map((g) => {
    const before = g.cabinets ?? [];
    const after = before.filter((c) => {
      if (c._id === cabinetId) { moved = c; return false; }
      return true;
    });
    return after === before ? g : { ...g, cabinets: after };
  });
  if (!moved) return p;
  return {
    ...p,
    garniturs: stripped.map((g) =>
      g._id === targetGarniturId
        ? { ...g, cabinets: [...(g.cabinets ?? []), moved as Cabinet] }
        : g,
    ),
  };
}

/** Bulk-add cabinets to a garnitur in one transaction (one counter bump
 *  per cabinet so ids stay unique). Returns the new project + the
 *  assigned ids in input order. */
export function addCabinets(
  p: Project,
  garniturId: Id,
  inits: Omit<Cabinet, "_id">[],
): { project: Project; cabinetIds: Id[] } {
  let cur = p;
  const out: Id[] = [];
  for (const init of inits) {
    const res = addCabinet(cur, garniturId, init);
    cur = res.project;
    out.push(res.cabinetId);
  }
  return { project: cur, cabinetIds: out };
}
